import { createAction } from 'redux-actions';
import { timeMonth, timeMinute } from 'd3-time';
import { isoParse, timeFormat } from 'd3-time-format';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import { checkForCachedDataAction, requestAction } from './request';
import fetch, {
  Api,
  defaultRequestConfig,
  getJson,
  handleError,
} from './fetch';

const omakantaDayFormat = timeFormat('%Y-%m-%d');
const omakantaTimeParse = isoParse;

export const supportedTypes = ['Observation', 'MedicationAdministration'];
// const observationCodes = ['15074-8', '14743-9', '14745-4', '9060-5', '9059-7'];

export const omakantaFetchAction = createAction('GET_OMAKANTA_DATA', payloadCreator, metaCreator);

    // check the medicationCodeableConcept for insulin type (and ignore other medicines)
    /*
    Human insulin zinc suspension crystalline (substance) {420837001 , SNOMED-CT }
    Human isophane insulin (substance) {421367005 , SNOMED-CT }
    Human regular insulin (substance) {420609005 , SNOMED-CT }
    Insulin aspart (substance) {325072002 , SNOMED-CT }
    Insulin aspart protamine (substance) {427292001 , SNOMED-CT }
    Insulin detemir (substance) {414515005 , SNOMED-CT }
    Insulin glargine (substance) {411529005 , SNOMED-CT }
    Insulin glulisine (substance) {411530000 , SNOMED-CT }
    Insulin lispro (substance) {412210000 , SNOMED-CT }
    Insulin lispro protamine (substance) {426313003 , SNOMED-CT }
    Lente human insulin (substance) {422346007 , SNOMED-CT }
    */

const fastActingCodes = [
  'ins-short-fast', // Omatietovaranto
  '411531001', // Short-acting insulin, removed: 2020-01-31
  '326077004', // Short with intermediate-acting insulins, removed: 2002-07-31
  '325013000', // Short-acting insulin product, removed: 2020-01-31
  '8780003', // Rapid-acting insulin, removed: 2005-07-31
  '126213004', // Rapid-acting insulin product, removed: 2005-07-31
  'A10AB', // ATC codes
];

const slowActingCodesCodes = [
  'ins-intermediate-long', // Omatietovaranto
  'A10AC', // ATC, Pitkävaikutteiset insuliinit
  '126215006', // SNOMED CT Long-acting insulin, removed: 2020-01-31
  '68475005', // SNOMED CT Intermediate-acting insulin, removed: 2020-01-31
  '710280006', // Ultra long-acting insulin, removed: 2020-01-31
  '25305005', // SNOMED Long-acting insulin
];

const isFastInsulin = codings => codings.some(coding => fastActingCodes.some(c => c === coding.code));
const isSlowInsulin = codings => codings.some(coding => slowActingCodesCodes.some(c => c === coding.code));

export const itemAdapter = (item, type, store) => {
  const identifier = item.identifier && item.identifier[0] && item.identifier[0].value;
  const parsed = {
    id: identifier || item.id,
    raw: item,
  };
  if (type === 'Observation') {
    parsed.time = omakantaTimeParse(item.effectiveDateTime).toISOString();
    // TODO: simplify coding, map to only codes!
    const codes = item.code.coding.map(c => c.code);
    if (codes.indexOf('14745-4') >= 0) {
      parsed.glucose = item.valueQuantity.value;
      parsed.unit = item.valueQuantity.unit;
      if (!store.sgv) {
        store.sgv = [];
      }
      store.sgv.push(parsed);
    } else if (codes.indexOf('15074-8') >= 0) {
      parsed.glucose = item.valueQuantity.value;
      parsed.unit = item.valueQuantity.unit;
      if (!store.smbg) {
        store.smbg = [];
      }
      store.smbg.push(parsed);
    } else if (codes.indexOf('9060-5') >= 0 || codes.indexOf('9059-7') >= 0) {
      if (item.valueQuantity) {
        parsed.carbohydrate = item.valueQuantity.value;
      } else if (item.component) {
        item.component.forEach((component) => {
          const componentCodes = component.code.coding.map(coding => coding.code);
          if (componentCodes.indexOf('9060-5') >= 0 || codes.indexOf('9059-7') >= 0) {
            parsed.carbohydrate = component.valueQuantity.value;
          }
        });
      }
      if (!store.nutrition) {
        store.nutrition = [];
      }
      store.nutrition.push(parsed);
    }
  } else if (type === 'MedicationAdministration') {
    const { medicationCodeableConcept = {} } = item;
    const { coding = [] } = medicationCodeableConcept;

    // check whether there's a point in time or a duration indicated
    if (item.effectivePeriod) {
      const start = omakantaTimeParse(item.effectivePeriod.start);
      parsed.time = start.toISOString();
      const end = omakantaTimeParse(item.effectivePeriod.end);
      parsed.endTime = end.toISOString();
      parsed.duration = timeMinute.count(parsed.time, parsed.endTime);
    } else {
      parsed.time = omakantaTimeParse(item.effectiveDateTime).toISOString();
    }

    const { dose = {}, rateRatio, rateQuantity } = item.dosage || {};
    if (dose.value) {
      parsed.amount = dose.value;
    }

    if (isFastInsulin(coding)) {
      // TODO: how are temporary basal rates expressed?
      if (rateRatio) {
        // basal
        const { numerator, denominator = {} } = rateRatio;
        const { value: time , unit } = denominator;
        const { value } = numerator;
        switch (unit.toLowerCase()) {
          case 'h':
          case 'hour':
          case 't':
            parsed.rate = value / time;
          break;
          case 'min':
          case 'minute':
          case 'm':
            parsed.rate = (value * 60) / time;
          break;
          default:
        }
        if (!store.basal) {
          store.basal = [];
        }
        store.basal.push(parsed);
      } else if (rateQuantity) {
        // also basal
        parsed.rate = rateQuantity.value;
        if (!store.basal) {
          store.basal = [];
        }
        store.basal.push(parsed);
      } else {
        // bolus
        if (!store.bolus) {
          store.bolus = [];
        }
        store.bolus.push(parsed);
      }
    } else if (isSlowInsulin(coding)) {
      // long acting insulin
      if (!store.longActing) {
        store.longActing = [];
      }
      store.longActing.push(parsed);
    } else {
      console.error('Unsupported medication administration entry', item, parsed);
    }
  }
  return store;
};

const getUrl = ({ type, code, params }) => {
  const dateParamName = type === 'MedicationAdministration' ? 'effective-time' : 'date';
  const codeString = code
  ? `&code=${code}`
  : '';
  const startTimeString = params.startTime
    ? `&${dateParamName}=ge${omakantaDayFormat(params.startTime)}`
    : '';
  const endTimeString = params.endTime
    ? `&${dateParamName}=lt${omakantaDayFormat(params.endTime)}`
    : '';
  const url = `${Api}/omakanta/${
    type
  }?_count=${
    type === 'Observation' ? 10000 : 3000
  }${
    codeString
  }&_sort=-${
    dateParamName
  }${
    startTimeString
  }${
    endTimeString
  }`;
  return url;
};

const fetchData = ({ type, params, url }) => {
  return fetch(url || getUrl({ type, params }), defaultRequestConfig)
    .then(handleError)
    .then(getJson)
    .then(json => {
      if (!json.entry) {
        return [];
      }
      const processed = json.entry.reduce((store, item) => {
        try {
          itemAdapter(item.resource, type, store);
        } catch (error) {
          console.error('Failed with item', item, error);
        }
        return store;
      }, {});
      if (json.link) {
        let nextLink;
        json.link.some((link) => {
          if (link.relation === 'next') {
            nextLink = link.url;
            return true;
          }
          return false;
        })
        if (nextLink) {
          processed.next = nextLink;
        }
      }
      return processed;
    });
};

const defaultParams = {
  startTime: timeMonth(new Date()),
};

export const getOmakantaData = ({ type, params = defaultParams, url, counter = 0 }) => ((dispatch) => {
  if (!type) {
    return Promise.reject(new Error('No type specified'));
  }
  const id = idCreator();
  const description = descriptionCreator({ source: `Omakanta${url ? ` - ${url}` : ''}`, type, params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetchData({ type, params, url }))
    .then((data) => {
      // Check if there's a next link present
      const stored = dispatch(omakantaFetchAction({ id, description, data }));
      if (data.next && counter < 20) {
        counter += 1;
        const search = data.next.substring(data.next.indexOf('?'));
        const nextUrl = `${Api}/omakanta${search}`;
        return dispatch(getOmakantaData({type, params, url: nextUrl, counter }));
      }
      return stored;
    })
    .then(data => dispatch(omakantaFetchAction({ id, description, data })))
    .catch(error => dispatch(omakantaFetchAction({ id, description, error: error.message || error })));
});

export default getOmakantaData;
